import { useEffect } from "react";
import { useAppIconSlots, getSlotUrl } from "@/hooks/useAppIconSlots";

const VITOLIER_THEME_COLOR = "#794939";

/**
 * Dynamically injects favicon, apple-touch-icon, theme-color, and
 * Apple PWA meta tags based on QA-approved app icon slots.
 * Falls back to static HTML-defined icons when no slots are populated.
 */
export function DynamicFavicon() {
  const { data: slots } = useAppIconSlots();

  useEffect(() => {
    if (!slots) return;

    const head = document.head;

    const upsertLink = (rel: string, href: string, sizes?: string, type?: string) => {
      let selector = `link[rel="${rel}"]`;
      if (sizes) selector += `[sizes="${sizes}"]`;
      let el = head.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (sizes) el.setAttribute("sizes", sizes);
        head.appendChild(el);
      }
      el.setAttribute("href", href);
      if (type) el.setAttribute("type", type);
    };

    const upsertMeta = (name: string, content: string) => {
      let el = head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Favicons
    const fav32 = getSlotUrl(slots, "favicon-32");
    const fav16 = getSlotUrl(slots, "favicon-16");
    if (fav32) {
      upsertLink("icon", fav32, undefined, "image/png");
      upsertLink("icon", fav32, "32x32", "image/png");
    }
    if (fav16) upsertLink("icon", fav16, "16x16", "image/png");

    // Apple touch icon
    const apple180 = getSlotUrl(slots, "apple-180");
    if (apple180) upsertLink("apple-touch-icon", apple180, "180x180");

    // PWA meta tags (always set)
    upsertMeta("theme-color", VITOLIER_THEME_COLOR);
    upsertMeta("apple-mobile-web-app-title", "Vitolier");
    upsertMeta("apple-mobile-web-app-capable", "yes");
    upsertMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
    upsertMeta("mobile-web-app-capable", "yes");
  }, [slots]);

  return null;
}
